import {
  BKJListItem,
  BKJSelect,
  BKJTextArea,
  BKJCheckbox,
  BKJIcon,
} from '@bookjane2/bookjane-design-library';
import { AutoMargin } from 'components/AutoMargin';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import { useShiftModalDataContext } from 'modals/ShiftModal/ShiftModalProvider.data.context';
import { releaseReasons } from 'modals/ShiftModal/src/views/ReleaseShiftView/ReleaseShiftView.constant';
import {
  useReleaseShiftViewContext,
  ReleaseShiftViewProvider,
} from 'modals/ShiftModal/src/views/ReleaseShiftView/ReleaseShiftView.context';
import {
  FormGroup,
  InnerWrapper,
  ReleaseContent,
  ReleaseShiftTitle,
  ReleaseShiftViewWrapper,
  ConfirmationButton,
  AddTimeOffSectionWrapper,
  AddTimeOffTitle,
  AddTimeOffDescription,
  AddTimeOffCheckbox,
  AddTimeOffCheckboxLabel,
} from 'modals/ShiftModal/src/views/ReleaseShiftView/ReleaseShiftView.styled';
import { FormLabel } from 'pages/CreateShiftsPage/BulkCreateShiftView/CreateShiftCard/CreateShiftCard.styled';
import { ReleaseShiftViewWarning } from 'modals/ShiftModal/src/views/ReleaseShiftView/ReleaseShiftViewWarning';
import { useTranslation } from 'hooks/useTranslation';
import { FC, Fragment, useCallback, useMemo } from 'react';
import { OrderStatusEnum } from 'types/graphql';
import { getTranslatedResponse } from 'utils/getTranslatedResponse';
import { useSelector } from 'react-redux';
import { FEATURE_SELECTORS_MAP } from 'guards/FeatureGuard.constants';
import { isTimeOffReadAccessForFacilitySelector } from 'store/selectors/scheduleSelectors/isTimeOffReadAccessForFacilitySelector';
import { ICustomFields } from 'modals/TimeOffModal/TimeOffModal.constants';
import { createTimeOffPayloadTransformer } from 'modals/TimeOffModal/views/CreateTimeOffView/CreateTimeOffView.constants';
import { ReleaseWithTimeOffPayloadType } from 'requests/index.types';
import { Flex } from 'components/Flex';
import { ShiftCustomFieldInput } from 'modals/ShiftModal/src/components/ShiftCustomFieldInput/ShiftCustomFieldInput';
import { MobileLabel } from 'pages/CreateShiftsPage/BulkCreateShiftView/CreateShiftModal/src/views/DetailsView/DetailsView.styled';
import { shiftModalEditViewFormItemVerticalMargin } from 'modals/ShiftModal/src/views';
import { SectionDivider } from 'modals/ShiftModal/ShiftModal.styled';
import {
  AllDaySection,
  TimeOffDateTimeWrapper,
  TimeOffRow,
  TimeOffColumn,
  NotesSection,
} from 'modals/TimeOffModal/TimeOffModalView.styled';
import { ShiftDateCalendarInput } from 'components/ShiftDateCalendarInput';
import { ShiftTimeSelectInput } from 'components/ShiftTimeSelectInput';
import { titleOptions, calendarDropdownProps } from 'modals/TimeOffModal/TimeOffModal.constants';
import { css } from 'styled-components';
import { withMemo } from 'utils/withMemo';
import { IBKJSelectRenderOptionsProps } from '@bookjane2/bookjane-design-library/lib/components/BKJSelect/BKJSelect.types';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import { useShiftModalPropsContext } from 'modals/ShiftModal/ShiftModal.props.context';
import { useIsAgencyShift } from 'hooks/useAgencyHooks';

const ReleaseShiftViewInner: FC = () => {
  const { t, i18n } = useTranslation();

  const {
    values,
    onChange,
    isDisabled: isReleaseFormDisabled,
    submitRelease,
    isSubmitting,
    fieldErrors,
    timeOffCustomFields,
  } = useReleaseShiftViewContext();
  const { data } = useShiftModalDataContext();
  const order = data?.order;
  const { isSharedJanePrimaryCommunityDifferentFromSelectedLocation } = useShiftModalPropsContext();

  const status = order?.status;
  const mainJane = order?.mainJane;
  const currentlySelectedLocation = getCurrentlySelectedLocation();
  const facilityId = Number.parseInt(currentlySelectedLocation.value);

  const isCustomFieldsEnabled =
    useSelector(FEATURE_SELECTORS_MAP['FEATURE_TYPE_CUSTOM_FIELDS']) && !order?.isExternal;
  const isTimeOffFeatureEnabled = useSelector(isTimeOffReadAccessForFacilitySelector(facilityId));

  const isAgencyShift = useIsAgencyShift({
    mainJane,
    sentToAgencies: order.sentToAgencies,
    orderCommunityOrgId: order.community?.organization?.id,
  });

  const isAgencyTeamMember = isAgencyShift && !!mainJane;

  const shouldShowTimeOffSection =
    values.reason_category === 'paid_time_off' || values.reason_category === 'unpaid_time_off';

  const timeOffSectionVisible =
    shouldShowTimeOffSection &&
    !!mainJane?.id &&
    !isAgencyTeamMember &&
    !isSharedJanePrimaryCommunityDifferentFromSelectedLocation &&
    isTimeOffFeatureEnabled;

  const { timeOffPayload } = useReleaseShiftTimeOffData({
    mainJaneId: mainJane?.id,
    shouldValidate: timeOffSectionVisible,
  });

  const dontAddTimeOff = values.dontAddTimeOff;
  const { startDate, startTime, endDate, endTime, allDay, title, notes = '' } = values;

  const translatedTimeOffOptions = useMemo(
    () =>
      titleOptions.map((option) => ({
        ...option,
        label: t(option.label),
      })),
    [t],
  );

  const RenderTimeOffOptions: FC<IBKJSelectRenderOptionsProps> = withMemo(
    function _RenderTimeOffOptions(props) {
      return (
        <Fragment>
          {translatedTimeOffOptions.map((option) => {
            const { type, ...restProps } = props;
            return <BKJListItem {...restProps} {...option} />;
          })}
        </Fragment>
      );
    },
  );

  const isShiftAssignedOrAcceptedOrSwapped = status?.value
    ? [OrderStatusEnum.assigned, OrderStatusEnum.accepted, OrderStatusEnum.swapped].includes(
        status.value,
      )
    : false;

  const transformedReleaseReasons = releaseReasons.filter((reason) => {
    if (isShiftAssignedOrAcceptedOrSwapped)
      return reason.value !== 'communicated_absence' && reason.value !== 'no_show';
    return releaseReasons;
  });

  const releaseButtonDisabled =
    isReleaseFormDisabled ||
    isSubmitting ||
    (timeOffPayload.shouldInclude && (timeOffPayload.hasErrors || !timeOffPayload.payload));

  const handleReleaseClick = useCallback(() => {
    submitRelease(timeOffPayload.shouldInclude ? timeOffPayload.payload : undefined);
  }, [submitRelease, timeOffPayload]);

  return (
    <Fragment>
      <ReleaseShiftViewWrapper flexDirection="column" flex="1">
        <ReleaseShiftTitle>{t('shift_modal:RELEASE_TEAM_MEMBER_CONFIRMATION')}</ReleaseShiftTitle>
        <InnerWrapper>
          <ReleaseShiftViewWarning />
          <FormGroup>
            <FormLabel>{t('shift_modal:REASON_FOR_RELEASE')}:*</FormLabel>
            <BKJSelect
              options={transformedReleaseReasons}
              type="Single"
              width="100%"
              variant="GreyOutlined"
              placeholder={t('shift_modal:SELECT_A_REASON')}
              value={[values.reason_category]}
              name="reason_category"
              required
              onChange={onChange}
              renderOptions={({ options, selected, onChange }) => {
                return (
                  <Fragment>
                    {transformedReleaseReasons.map(({ key, label, value }) => {
                      const translatedLabel = getTranslatedResponse(label, 'status', i18n, t);
                      return (
                        <BKJListItem
                          name="reason_category"
                          selected={selected}
                          key={key}
                          label={translatedLabel}
                          value={value}
                          onChange={onChange}
                        />
                      );
                    })}
                  </Fragment>
                );
              }}
            />
          </FormGroup>
          {values.reason_category === 'other' && (
            <FormGroup>
              <FormLabel>{t('shift_modal:PLEASE_SPECIFY_REASON')}:*</FormLabel>
              <BKJTextArea
                name="reason_message"
                placeholder={t('shift_modal:REASON_VISIBLE_TO_FACILITY_AND_TEAM_MEMBER')}
                value={values.reason_message}
                onChange={onChange}
                maxLength={500}
              />
            </FormGroup>
          )}

          <ReleaseContent>
            {isAgencyTeamMember
              ? t('shift_modal:AGENCY_CUSTOMER_AND_TEAM_MEMBER_WILL_BE_NOTIFIED')
              : t('shift_modal:CUSTOMER_AND_TEAM_MEMBER_WILL_BE_NOTIFIED')}
          </ReleaseContent>
          {timeOffSectionVisible && (
            <AddTimeOffSectionWrapper>
              <AddTimeOffTitle>{t('schedule:ADD_TIME_OFF')}</AddTimeOffTitle>
              <AddTimeOffDescription>
                {t('schedule:ADDING_TIME_OFF_WILL_BLOCK_THIS_TEAM_MEMBER')}
              </AddTimeOffDescription>
              <AddTimeOffCheckbox>
                <BKJCheckbox
                  value={dontAddTimeOff}
                  name="dontAddTimeOff"
                  variant="GreenSolid"
                  onChange={onChange}
                />
                <AddTimeOffCheckboxLabel>{t('schedule:DONT_ADD_TIME_OFF')}</AddTimeOffCheckboxLabel>
              </AddTimeOffCheckbox>

              {!dontAddTimeOff && (
                <Fragment>
                  <Flex width="100%" flexDirection="column">
                    <FormLabel>{t('schedule:TIME_OFF_TYPE')}*</FormLabel>
                    <BKJSelect
                      options={translatedTimeOffOptions}
                      type="Single"
                      variant="GreyOutlined"
                      dropdownPlacement="bottom-start"
                      name="title"
                      onChange={onChange}
                      value={title}
                      renderOptions={RenderTimeOffOptions}
                      required
                    />
                  </Flex>

                  <AllDaySection>
                    <BKJCheckbox
                      value={allDay}
                      name="allDay"
                      variant="GreenSolid"
                      onChange={onChange}
                      css={css`
                        margin-right: 12px;
                      `}
                    />
                    {t('schedule:ALL_DAY')}
                  </AllDaySection>

                  <Flex flexDirection="column" width="100%" gap="8px">
                    <TimeOffDateTimeWrapper>
                      <TimeOffRow>
                        <TimeOffColumn>
                          <FormLabel>{t('schedule:STARTS')}</FormLabel>
                          <ShiftDateCalendarInput
                            name="startDate"
                            iconComponent={BKJIcon}
                            iconProps={{
                              iconName: 'ShiftsDate',
                            }}
                            error={fieldErrors?.startDate}
                            value={startDate}
                            onChange={onChange}
                            dropdownProps={calendarDropdownProps}
                          />
                        </TimeOffColumn>
                        <TimeOffColumn>
                          {!allDay && (
                            <ShiftTimeSelectInput
                              label={t('schedule:START_TIME')}
                              value={startTime}
                              placeholder={t('schedule:START_TIME')}
                              onChange={onChange}
                              error={fieldErrors?.startTime}
                              name="startTime"
                            />
                          )}
                        </TimeOffColumn>
                      </TimeOffRow>
                      <TimeOffRow>
                        <TimeOffColumn>
                          <FormLabel>{t('schedule:ENDS')}</FormLabel>
                          <ShiftDateCalendarInput
                            iconComponent={BKJIcon}
                            iconProps={{
                              iconName: 'ShiftsDate',
                            }}
                            error={fieldErrors?.endDate}
                            name="endDate"
                            value={endDate}
                            onChange={onChange}
                            dropdownProps={calendarDropdownProps}
                          />
                        </TimeOffColumn>
                        <TimeOffColumn>
                          {!allDay && (
                            <ShiftTimeSelectInput
                              label={t('schedule:END_TIME')}
                              value={endTime}
                              placeholder={t('schedule:END_TIME')}
                              onChange={onChange}
                              name="endTime"
                              error={fieldErrors?.endTime}
                            />
                          )}
                        </TimeOffColumn>
                      </TimeOffRow>
                    </TimeOffDateTimeWrapper>
                  </Flex>

                  <NotesSection>
                    <BKJTextArea
                      label={t('schedule:NOTES')}
                      name="notes"
                      placeholder={t(
                        'schedule:NOTES_WILL_ONLY_BE_SHARED_WITH_FACILITY_ADMINISTRATORS',
                      )}
                      onChange={onChange}
                      maxLength={1000}
                      value={notes}
                    />
                  </NotesSection>

                  {isCustomFieldsEnabled && timeOffCustomFields.length > 0 && (
                    <Fragment>
                      <SectionDivider
                        variant="Thin"
                        margin={`${shiftModalEditViewFormItemVerticalMargin}px 0`}
                      />
                      {timeOffCustomFields.map((customField) => {
                        return (
                          <Flex
                            key={customField.id}
                            flexDirection="column"
                            margin={`0 0 ${shiftModalEditViewFormItemVerticalMargin}px 0`}
                          >
                            <FormLabel>
                              {customField.name}
                              {customField.mobile && (
                                <MobileLabel>
                                  &nbsp;({t('schedule:SHOWN_INTERNALLY_ON_MOBILE')})
                                </MobileLabel>
                              )}
                            </FormLabel>
                            <ShiftCustomFieldInput
                              key={customField.id}
                              customField={customField}
                              value={(values?.[customField?.name] as string) ?? ''}
                              onChange={onChange}
                            />
                          </Flex>
                        );
                      })}
                    </Fragment>
                  )}
                </Fragment>
              )}
            </AddTimeOffSectionWrapper>
          )}
        </InnerWrapper>
      </ReleaseShiftViewWrapper>
      <BKJSideModalFooter>
        <AutoMargin />
        <ConfirmationButton
          width="180px"
          variant="PurpleSolid"
          disabled={releaseButtonDisabled}
          isLoading={isSubmitting}
          onClick={handleReleaseClick}
        >
          {t('shift_modal:RELEASE')}
        </ConfirmationButton>
      </BKJSideModalFooter>
    </Fragment>
  );
};

export const ReleaseShiftView: FC = () => {
  return (
    <ReleaseShiftViewProvider>
      <ReleaseShiftViewInner />
    </ReleaseShiftViewProvider>
  );
};

type UseReleaseShiftTimeOffDataArgs = {
  mainJaneId?: string;
  shouldValidate: boolean;
};

const RESERVED_TIME_OFF_KEYS = new Set([
  'dontAddTimeOff',
  'notes',
  'title',
  'allDay',
  'startDate',
  'startTime',
  'endDate',
  'endTime',
  'reason_category',
  'reason_message',
  'recurring',
]);

const useReleaseShiftTimeOffData = ({
  mainJaneId,
  shouldValidate,
}: UseReleaseShiftTimeOffDataArgs) => {
  const timeOffContext = useReleaseShiftViewContext();
  const currentlySelectedLocation = getCurrentlySelectedLocation();
  const selectedFacilityId = Number.parseInt(currentlySelectedLocation.value);
  const { values, hasFieldErrors, timeOffCustomFields } = timeOffContext;

  const dontAddTimeOff = values.dontAddTimeOff;
  const shouldValidateAndInclude = shouldValidate && !dontAddTimeOff && !!mainJaneId;

  const timeOffPayload = useMemo<ReleaseWithTimeOffPayloadType | undefined>(() => {
    if (!shouldValidateAndInclude) return undefined;

    const {
      dontAddTimeOff,
      allDay,
      startDate,
      startTime,
      endDate,
      endTime,
      title,
      notes,
      ...rest
    } = values;

    const customFieldsValues = Object.keys(rest).reduce((acc, key) => {
      if (RESERVED_TIME_OFF_KEYS.has(key)) return acc;
      const value = rest[key];
      acc[key] = Array.isArray(value) ? value[0] || '' : (value as string);
      return acc;
    }, {} as ICustomFields);

    const transformed = createTimeOffPayloadTransformer({
      jane_id: mainJaneId!,
      allDay: allDay as boolean,
      startDate: startDate as string[],
      endDate: endDate as string[],
      startTime: startTime as string[],
      endTime: endTime as string[],
      title: title as string[],
      notes: (notes as string) || '',
      selectedFacilityId: selectedFacilityId ?? 0,
      customFields: customFieldsValues,
      timeOffCustomFields,
    });

    const releasePayload: ReleaseWithTimeOffPayloadType = {
      start_date_time: transformed.time_off.start_date_time,
      end_date_time: transformed.time_off.end_date_time,
      title: transformed.time_off.title,
      notes: transformed.time_off.notes,
      custom_fields: transformed.time_off.custom_fields?.length
        ? transformed.time_off.custom_fields
        : undefined,
    };

    return releasePayload;
  }, [shouldValidateAndInclude, mainJaneId, values, selectedFacilityId, timeOffCustomFields]);

  return {
    timeOffValidationErrors: undefined,
    timeOffValidationPending: false,
    timeOffPayload: {
      shouldInclude: shouldValidateAndInclude,
      hasErrors: hasFieldErrors,
      payload: shouldValidateAndInclude ? timeOffPayload : undefined,
    },
  };
};
